import React, { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { RHAdmin } from "../components/RHAdmin";

import {
  FileText,
  Calendar,
  CircleAlert as AlertCircle,
  Clock,
  CircleCheck as CheckCircle,
  Circle as XCircle,
  Loader as Loader2,
  File,
  Users,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { useGamification } from "../contexts/GamificationContext";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";
import { format, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

type TipoSolicitacao = "ausencia" | "atestado" | "ajuste_ponto";

interface Solicitacao {
  id: string;
  usuario_id: string;
  tipo: TipoSolicitacao;
  descricao: string;
  data_inicio: string | null;
  data_fim: string | null;
  arquivo_url: string | null;
  status: "pendente" | "aprovado" | "rejeitado";
  data_solicitacao: string;
  resposta_rh: string | null;
  dados_especificos: any;
}

// rótulos/descrições/ícones
const tipoLabels: Record<TipoSolicitacao, string> = {
  ausencia: "Comunicação de Ausência",
  atestado: "Envio de Atestado Médico",
  ajuste_ponto: "Solicitação de Ajuste de Ponto",
};

const tipoDescricoes: Record<TipoSolicitacao, string> = {
  ausencia: "Informe ausências programadas durante o expediente",
  atestado: "Envie seu atestado médico em até 48 horas",
  ajuste_ponto: "Solicite correções no registro de ponto eletrônico",
};

const tipoIcons: Record<TipoSolicitacao, React.ReactNode> = {
  ausencia: <AlertCircle className="w-5 h-5" />,
  atestado: <FileText className="w-5 h-5" />,
  ajuste_ponto: <Clock className="w-5 h-5" />,
};

export const RH: React.FC = () => {
  const { user } = useAuth();
  const { addActivity } = useGamification();

  const [activeTab, setActiveTab] = useState<"solicitacoes" | "minhas" | "admin">("solicitacoes");
  const isRH = user?.setor === "RH" || user?.role === "admin";

  const [tipoAtivo, setTipoAtivo] = useState<TipoSolicitacao>("ausencia");
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [minhasSolicitacoes, setMinhasSolicitacoes] = useState<Solicitacao[]>([]);

  // ==============================
  // ESTADOS DOS FORMULÁRIOS
  // ==============================
  const [ausenciaForm, setAusenciaForm] = useState({
    data_ausencia: "",
    horario_saida: "",
    horario_retorno: "",
    motivo: "",
    responsavel_setor: "",
    transporte_empresa: "",
    tipo_ausencia: "",
    horas_previstas: "",
    justificativa_detalhada: "",
  });

  const [atestadoForm, setAtestadoForm] = useState({
    data_inicio: "",
    dias_afastamento: "",
    data_retorno: "",
    nome_medico: "",
    crm: "",
    cid: "",
    tipo_atendimento: "",
    via_convenio: "",
    local_atendimento: "",
    acompanhamento_psicologico: "",
    tipo_afastamento: "",
    nome_paciente: "",
    recomendacao_medica: "",
    necessita_substituicao: "",
    justificativa_sem_atestado: "",
    observacoes: "",
    arquivo: null as File | null,
  });

  const [ajustePontoForm, setAjustePontoForm] = useState({
    data_ajuste: "",
    horario_entrada_correto: "",
    horario_saida_correto: "",
    horario_registrado: "",
    turno: "",
    supervisor: "",
    comunicou_lider: "",
    obs_lider: "",
    comprovante: null as File | null,
    observacoes: "",
    periodo_afetado: "",
    justificativa_detalhada: "",
  });

  // ==============================
  // CARREGAR SOLICITAÇÕES DO USUÁRIO
  // ==============================
  useEffect(() => {
    if (activeTab === "minhas") loadMinhasSolicitacoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user]);

  const loadMinhasSolicitacoes = async () => {
    if (!user?.dbId) return;
    try {
      const { data, error } = await supabase
        .from("rh_solicitacoes")
        .select("*")
        .eq("usuario_id", user.dbId)
        .order("data_solicitacao", { ascending: false });

      if (error) throw error;
      setMinhasSolicitacoes(data || []);
    } catch {
      toast.error("Erro ao carregar suas solicitações");
    }
  };

  // ==============================
  // UTILITÁRIOS
  // ==============================
  const resetForms = () => {
    setAusenciaForm({
      data_ausencia: "",
      horario_saida: "",
      horario_retorno: "",
      motivo: "",
      responsavel_setor: "",
      transporte_empresa: "",
      tipo_ausencia: "",
      horas_previstas: "",
      justificativa_detalhada: "",
    });

    setAtestadoForm({
      data_inicio: "",
      dias_afastamento: "",
      data_retorno: "",
      nome_medico: "",
      crm: "",
      cid: "",
      tipo_atendimento: "",
      via_convenio: "",
      local_atendimento: "",
      acompanhamento_psicologico: "",
      tipo_afastamento: "",
      nome_paciente: "",
      recomendacao_medica: "",
      necessita_substituicao: "",
      justificativa_sem_atestado: "",
      observacoes: "",
      arquivo: null,
    });

    setAjustePontoForm({
      data_ajuste: "",
      horario_entrada_correto: "",
      horario_saida_correto: "",
      horario_registrado: "",
      turno: "",
      supervisor: "",
      comunicou_lider: "",
      obs_lider: "",
      comprovante: null,
      observacoes: "",
      periodo_afetado: "",
      justificativa_detalhada: "",
    });
  };

  const handleAtestadoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const max = 5 * 1024 * 1024;
    if (f.size > max) return toast.error("Arquivo muito grande (máx. 5MB)");

    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowed.includes(f.type)) return toast.error("Tipo de arquivo não permitido");

    setAtestadoForm((s) => ({ ...s, arquivo: f }));
  };

  const handleAjusteComprovanteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (f) {
      const max = 5 * 1024 * 1024;
      if (f.size > max) return toast.error("Arquivo muito grande (máx. 5MB)");
      const allowed = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowed.includes(f.type)) return toast.error("Tipo de arquivo não permitido");
    }
    setAjustePontoForm((s) => ({ ...s, comprovante: f }));
  };

  const uploadFile = async (file: File, bucket: string) => {
    try {
      setUploadingFile(true);

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser) {
        toast.error("Usuário não autenticado");
        return null;
      }

      const ext = file.name.split(".").pop();
      const fileName = `${authUser.id}/${Date.now()}.${ext}`;

      const { error } = await supabase.storage.from(bucket).upload(fileName, file);
      if (error) {
        console.error("Erro no upload:", error);
        throw error;
      }

      return `${bucket}/${fileName}`;
    } catch (e: any) {
      console.error("Erro detalhado no upload:", e);
      toast.error(`Erro ao enviar arquivo: ${e.message || "Erro desconhecido"}`);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const getSignedUrl = async (filePath: string) => {
    try {
      let bucket: string;
      let path: string;

      if (filePath.startsWith("http")) {
        const urlParts = filePath.split("/object/public/");
        if (urlParts.length !== 2) throw new Error("URL inválida");
        const [bucketName, ...pathParts] = urlParts[1].split("/");
        bucket = bucketName;
        path = pathParts.join("/");
      } else {
        const [bucketName, ...pathParts] = filePath.split("/");
        bucket = bucketName;
        path = pathParts.join("/");
      }

      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
      if (error) throw error;

      return data.signedUrl;
    } catch (error: any) {
      console.error("[RH] Erro detalhado ao gerar URL assinada:", error);
      return null;
    }
  };

  const abrirDocumento = async (filePath: string) => {
    const url = await getSignedUrl(filePath);
    if (url) window.open(url, "_blank");
    else toast.error("Erro ao abrir documento. Verifique o console para mais detalhes.");
  };

  // ==============================
  // SUBMITS
  // ==============================
  const handleSubmitAusencia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.dbId) return toast.error("Usuário não encontrado");
    if (!ausenciaForm.data_ausencia || !ausenciaForm.motivo) return toast.error("Preencha os campos obrigatórios");

    setLoading(true);
    try {
      const dataOk = isValid(new Date(ausenciaForm.data_ausencia));
      const descricao = `Ausência em ${
        dataOk ? format(new Date(ausenciaForm.data_ausencia), "dd/MM/yyyy") : ausenciaForm.data_ausencia
      } - ${ausenciaForm.motivo}`;

      const dataInsert = {
        usuario_id: user.dbId,
        tipo: "ausencia",
        descricao,
        status: "pendente",
        data_inicio: ausenciaForm.data_ausencia || null,
        data_fim: null,
        dados_especificos: {
          horario_saida: ausenciaForm.horario_saida || null,
          horario_retorno: ausenciaForm.horario_retorno || null,
          motivo: ausenciaForm.motivo || null,
          responsavel_setor: ausenciaForm.responsavel_setor || null,
          transporte_empresa: ausenciaForm.transporte_empresa || null,
          tipo: ausenciaForm.tipo_ausencia || null,
          horas_previstas: ausenciaForm.horas_previstas ? Number(ausenciaForm.horas_previstas) : null,
          justificativa_detalhada: ausenciaForm.justificativa_detalhada || null,
        },
      };

      const { error } = await supabase.from("rh_solicitacoes").insert(dataInsert);
      if (error) throw error;

      addActivity("hr_request", "Comunicação de ausência", { tipo: "ausencia" });
      toast.success("Ausência enviada! +3 pontos");
      resetForms();
      loadMinhasSolicitacoes();
    } catch {
      toast.error("Erro ao enviar");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAtestado = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.dbId) return toast.error("Usuário não encontrado");
    if (!atestadoForm.data_inicio) return toast.error("Informe a data de início");
    if (!atestadoForm.arquivo && !atestadoForm.justificativa_sem_atestado) {
      return toast.error("Envie o arquivo do atestado ou preencha a justificativa");
    }

    setLoading(true);
    try {
      let url: string | null = null;
      if (atestadoForm.arquivo) {
        url = await uploadFile(atestadoForm.arquivo, "rh-atestados");
        if (!url) return;
      }

      const inicioOk = isValid(new Date(atestadoForm.data_inicio));
      const descricao = `Atestado médico - ${atestadoForm.dias_afastamento || "?"} dias a partir de ${
        inicioOk ? format(new Date(atestadoForm.data_inicio), "dd/MM/yyyy") : atestadoForm.data_inicio
      }`;

      const dataInsert = {
        usuario_id: user.dbId,
        tipo: "atestado",
        descricao,
        status: "pendente",
        data_inicio: atestadoForm.data_inicio || null,
        data_fim: atestadoForm.data_retorno || null,
        arquivo_url: url,
        dados_especificos: {
          dias_afastamento: atestadoForm.dias_afastamento ? Number(atestadoForm.dias_afastamento) : null,
          nome_medico: atestadoForm.nome_medico || null,
          crm: atestadoForm.crm || null,
          cid: atestadoForm.cid || null,
          tipo_atendimento: atestadoForm.tipo_atendimento || null,
          via_convenio: atestadoForm.via_convenio || null,
          local_atendimento: atestadoForm.local_atendimento || null,
          acompanhamento_psicologico: atestadoForm.acompanhamento_psicologico || null,
          tipo_afastamento: atestadoForm.tipo_afastamento || null,
          nome_paciente: atestadoForm.nome_paciente || null,
          recomendacao_medica: atestadoForm.recomendacao_medica || null,
          necessita_substituicao: atestadoForm.necessita_substituicao || null,
          justificativa_sem_arquivo: atestadoForm.justificativa_sem_atestado || null,
          observacoes: atestadoForm.observacoes || null,
        },
      };

      const { error } = await supabase.from("rh_solicitacoes").insert(dataInsert);
      if (error) throw error;

      addActivity("hr_request", "Envio de atestado médico", { tipo: "atestado" });
      toast.success("Atestado enviado! +3 pontos");
      resetForms();
      loadMinhasSolicitacoes();
    } catch {
      toast.error("Erro ao enviar atestado");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAjustePonto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.dbId) return toast.error("Usuário não encontrado");
    if (!ajustePontoForm.data_ajuste) return toast.error("Preencha os campos obrigatórios");

    setLoading(true);
    try {
      let comprovanteUrl: string | null = null;
      if (ajustePontoForm.comprovante) {
        comprovanteUrl = await uploadFile(ajustePontoForm.comprovante, "rh-comprovantes");
        if (!comprovanteUrl) return;
      }

      const dataOk = isValid(new Date(ajustePontoForm.data_ajuste));
      const descricao = `Ajuste de ponto - ${
        dataOk ? format(new Date(ajustePontoForm.data_ajuste), "dd/MM/yyyy") : ajustePontoForm.data_ajuste
      }`;

      const dataInsert = {
        usuario_id: user.dbId,
        tipo: "ajuste_ponto",
        descricao,
        status: "pendente",
        data_inicio: ajustePontoForm.data_ajuste || null,
        arquivo_url: comprovanteUrl,
        dados_especificos: {
          horario_entrada_correto: ajustePontoForm.horario_entrada_correto || null,
          horario_saida_correto: ajustePontoForm.horario_saida_correto || null,
          horario_registrado: ajustePontoForm.horario_registrado || null,
          turno: ajustePontoForm.turno || null,
          supervisor: ajustePontoForm.supervisor || null,
          comunicou_lider: ajustePontoForm.comunicou_lider || null,
          obs_lider: ajustePontoForm.obs_lider || null,
          observacoes: ajustePontoForm.observacoes || null,
          periodo_afetado: ajustePontoForm.periodo_afetado || null,
          justificativa_detalhada: ajustePontoForm.justificativa_detalhada || null,
        },
      };

      const { error } = await supabase.from("rh_solicitacoes").insert(dataInsert);
      if (error) throw error;

      addActivity("hr_request", "Solicitou ajuste de ponto", { tipo: "ajuste_ponto" });
      toast.success("Ajuste enviado! +3 pontos");
      resetForms();
      loadMinhasSolicitacoes();
    } catch {
      toast.error("Erro ao enviar ajuste");
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // UI HELPERS
  // ==============================
  const getStatusBadge = (status: string) => {
    const map = {
      pendente: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-800 dark:text-yellow-300", icon: <Clock className="w-3 h-3" /> },
      aprovado: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-800 dark:text-green-300", icon: <CheckCircle className="w-3 h-3" /> },
      rejeitado: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-800 dark:text-red-300", icon: <XCircle className="w-3 h-3" /> },
    } as const;

    const b = map[status as keyof typeof map];
    return (
      <span
        className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${b.bg} ${b.text}`}
      >
        {b.icon}
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  // ==============================
  // FORMS
  // ==============================
  const renderFormAusencia = () => (
    <form onSubmit={handleSubmitAusencia} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Data da Ausência *</label>
          <input
            type="date"
            value={ausenciaForm.data_ausencia}
            onChange={(e) => setAusenciaForm({ ...ausenciaForm, data_ausencia: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Motivo *</label>
          <select
            value={ausenciaForm.motivo}
            onChange={(e) => setAusenciaForm({ ...ausenciaForm, motivo: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Consulta Médica">Consulta Médica</option>
            <option value="Exame Médico">Exame Médico</option>
            <option value="Assuntos Pessoais">Assuntos Pessoais</option>
            <option value="Assuntos Bancários">Assuntos Bancários</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Tipo de ausência *</label>
          <select
            value={ausenciaForm.tipo_ausencia}
            onChange={(e) => setAusenciaForm({ ...ausenciaForm, tipo_ausencia: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Parcial">Parcial</option>
            <option value="Total">Total</option>
            <option value="Home Office">Home Office</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Horário de Saída</label>
          <input
            type="time"
            value={ausenciaForm.horario_saida}
            onChange={(e) => setAusenciaForm({ ...ausenciaForm, horario_saida: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Horário de Retorno</label>
          <input
            type="time"
            value={ausenciaForm.horario_retorno}
            onChange={(e) => setAusenciaForm({ ...ausenciaForm, horario_retorno: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Horas previstas (opcional)</label>
          <input
            type="number"
            min={1}
            value={ausenciaForm.horas_previstas}
            onChange={(e) => setAusenciaForm({ ...ausenciaForm, horas_previstas: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Ex: 3"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Responsável pelo setor</label>
        <input
          type="text"
          value={ausenciaForm.responsavel_setor}
          onChange={(e) => setAusenciaForm({ ...ausenciaForm, responsavel_setor: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Necessita transporte da empresa?</label>
        <select
          value={ausenciaForm.transporte_empresa}
          onChange={(e) => setAusenciaForm({ ...ausenciaForm, transporte_empresa: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Selecione</option>
          <option value="Sim">Sim</option>
          <option value="Não">Não</option>
        </select>
      </div>

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Justificativa detalhada</label>
        <textarea
          rows={3}
          value={ausenciaForm.justificativa_detalhada}
          onChange={(e) => setAusenciaForm({ ...ausenciaForm, justificativa_detalhada: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          placeholder="Descreva com mais detalhes o motivo da ausência"
        />
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={loading} className="px-6 py-3 bg-blue-600 text-white rounded-lg">
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
            </span>
          ) : (
            "Enviar Solicitação"
          )}
        </button>
        <button
          type="button"
          onClick={resetForms}
          className="px-6 py-3 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-lg"
        >
          Limpar campos
        </button>
      </div>
    </form>
  );

  const renderFormAtestado = () => (
    <form onSubmit={handleSubmitAtestado} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Data de Início *</label>
          <input
            type="date"
            value={atestadoForm.data_inicio}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, data_inicio: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Dias de afastamento</label>
          <input
            type="number"
            min={1}
            value={atestadoForm.dias_afastamento}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, dias_afastamento: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Ex: 2"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Data de retorno (se houver)</label>
          <input
            type="date"
            value={atestadoForm.data_retorno}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, data_retorno: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Tipo de atendimento</label>
          <select
            value={atestadoForm.tipo_atendimento}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, tipo_atendimento: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Consulta">Consulta</option>
            <option value="Emergência">Emergência</option>
            <option value="Internação">Internação</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Atendimento via convênio?</label>
          <select
            value={atestadoForm.via_convenio}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, via_convenio: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Instituição / Clínica</label>
          <input
            type="text"
            value={atestadoForm.local_atendimento}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, local_atendimento: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Ex: Hospital São Lucas"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Tipo de afastamento</label>
          <select
            value={atestadoForm.tipo_afastamento}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, tipo_afastamento: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Proprio">Próprio</option>
            <option value="Familiar">Familiar</option>
          </select>
        </div>

        {atestadoForm.tipo_afastamento === "Familiar" && (
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Nome do paciente</label>
            <input
              type="text"
              value={atestadoForm.nome_paciente}
              onChange={(e) => setAtestadoForm({ ...atestadoForm, nome_paciente: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Nome do dependente"
            />
          </div>
        )}

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Acompanhamento psicológico?</label>
          <select
            value={atestadoForm.acompanhamento_psicologico}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, acompanhamento_psicologico: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Necessita substituição temporária?</label>
          <select
            value={atestadoForm.necessita_substituicao}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, necessita_substituicao: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Nome do médico</label>
          <input
            type="text"
            value={atestadoForm.nome_medico}
            onChange={(e) => setAtestadoForm({ ...atestadoForm, nome_medico: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">CRM</label>
            <input
              type="text"
              value={atestadoForm.crm}
              onChange={(e) => setAtestadoForm({ ...atestadoForm, crm: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">CID</label>
            <input
              type="text"
              value={atestadoForm.cid}
              onChange={(e) => setAtestadoForm({ ...atestadoForm, cid: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Recomendação médica</label>
        <textarea
          rows={3}
          value={atestadoForm.recomendacao_medica}
          onChange={(e) => setAtestadoForm({ ...atestadoForm, recomendacao_medica: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Anexar atestado (PDF/JPG/PNG)</label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleAtestadoFileChange}
            className="text-gray-700 dark:text-gray-200"
          />
        </div>

        {!atestadoForm.arquivo && (
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Justificativa sem atestado</label>
            <textarea
              rows={3}
              value={atestadoForm.justificativa_sem_atestado}
              onChange={(e) => setAtestadoForm({ ...atestadoForm, justificativa_sem_atestado: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Explique por que não há documento"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Observações</label>
        <textarea
          rows={3}
          value={atestadoForm.observacoes}
          onChange={(e) => setAtestadoForm({ ...atestadoForm, observacoes: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={loading || uploadingFile} className="px-6 py-3 bg-green-600 text-white rounded-lg">
          {loading || uploadingFile ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
            </span>
          ) : (
            "Enviar Atestado"
          )}
        </button>

        <button
          type="button"
          onClick={resetForms}
          className="px-6 py-3 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-lg"
        >
          Limpar campos
        </button>
      </div>
    </form>
  );

  // ✅ AQUI está a correção: labels com text-gray + dark:text claro
  const renderFormAjuste = () => (
    <form onSubmit={handleSubmitAjustePonto} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Data *</label>
          <input
            type="date"
            value={ajustePontoForm.data_ajuste}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, data_ajuste: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Período afetado</label>
          <select
            value={ajustePontoForm.periodo_afetado}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, periodo_afetado: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Manhã">Manhã</option>
            <option value="Tarde">Tarde</option>
            <option value="Noite">Noite</option>
            <option value="Plantão">Plantão</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Turno</label>
          <select
            value={ajustePontoForm.turno}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, turno: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Manhã">Manhã</option>
            <option value="Tarde">Tarde</option>
            <option value="Noite">Noite</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Supervisor responsável</label>
          <input
            type="text"
            value={ajustePontoForm.supervisor}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, supervisor: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Comunicou o líder?</label>
          <select
            value={ajustePontoForm.comunicou_lider}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, comunicou_lider: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Selecione</option>
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Entrada correta</label>
          <input
            type="time"
            value={ajustePontoForm.horario_entrada_correto}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, horario_entrada_correto: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Saída correta</label>
          <input
            type="time"
            value={ajustePontoForm.horario_saida_correto}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, horario_saida_correto: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Horário registrado</label>
          <input
            type="time"
            value={ajustePontoForm.horario_registrado}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, horario_registrado: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Justificativa detalhada</label>
        <textarea
          rows={3}
          value={ajustePontoForm.justificativa_detalhada}
          onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, justificativa_detalhada: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Anexar comprovante (opcional)</label>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleAjusteComprovanteChange}
          className="text-gray-700 dark:text-gray-200"
        />
      </div>

      <div className="grid grid-cols-1">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-2">Observações</label>
          <textarea
            rows={2}
            value={ajustePontoForm.observacoes}
            onChange={(e) => setAjustePontoForm({ ...ajustePontoForm, observacoes: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={loading || uploadingFile} className="px-6 py-3 bg-purple-600 text-white rounded-lg">
          {loading || uploadingFile ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
            </span>
          ) : (
            "Enviar Ajuste"
          )}
        </button>

        <button
          type="button"
          onClick={resetForms}
          className="px-6 py-3 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-lg"
        >
          Limpar campos
        </button>
      </div>
    </form>
  );

  // ==============================
  // RENDER PRINCIPAL
  // ==============================
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-display text-headline-lg text-on-surface mb-4">Portal RH</h1>

        <div className="flex space-x-4 border-b border-outline-variant mb-6">
          <button
            onClick={() => setActiveTab("solicitacoes")}
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === "solicitacoes"
                ? "border-b-2 border-primary text-primary"
                : "text-secondary dark:text-gray-400 hover:text-on-surface dark:hover:text-gray-300"
            }`}
          >
            Nova Solicitação
          </button>

          <button
            onClick={() => setActiveTab("minhas")}
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === "minhas"
                ? "border-b-2 border-primary text-primary"
                : "text-secondary dark:text-gray-400 hover:text-on-surface dark:hover:text-gray-300"
            }`}
          >
            Minhas Solicitações
          </button>

          {isRH && (
            <button
              onClick={() => setActiveTab("admin")}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === "admin"
                  ? "border-b-2 border-primary text-primary"
                  : "text-secondary dark:text-gray-400 hover:text-on-surface dark:hover:text-gray-300"
              }`}
            >
              <Users className="w-4 h-4" />
              Gestão RH
            </button>
          )}
        </div>

        {activeTab === "solicitacoes" && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {(Object.keys(tipoLabels) as TipoSolicitacao[]).map((tipo) => (
                <button
                  key={tipo}
                  onClick={() => setTipoAtivo(tipo)}
                  className={`p-6 rounded-xl border-2 ${
                    tipoAtivo === tipo
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-outline-variant hover:border-outline-variant/50 bg-white dark:bg-gray-800"
                  }`}
                >
                  <div className="p-3 bg-surface-container rounded-lg w-fit mb-3">{tipoIcons[tipo]}</div>
                  <h3 className="font-semibold text-on-surface">{tipoLabels[tipo]}</h3>
                  <p className="text-xs text-secondary">{tipoDescricoes[tipo]}</p>
                </button>
              ))}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-outline-variant p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-primary/10 text-primary rounded-lg">{tipoIcons[tipoAtivo]}</div>
                <div>
                  <h3 className="text-lg font-bold text-on-surface">{tipoLabels[tipoAtivo]}</h3>
                  <p className="text-sm text-secondary">{tipoDescricoes[tipoAtivo]}</p>
                </div>
              </div>

              {tipoAtivo === "ausencia" && renderFormAusencia()}
              {tipoAtivo === "atestado" && renderFormAtestado()}
              {tipoAtivo === "ajuste_ponto" && renderFormAjuste()}
            </div>
          </div>
        )}

        {activeTab === "minhas" && (
          <div className="space-y-4">
            {minhasSolicitacoes.length === 0 ? (
              <div className="p-10 border border-outline-variant rounded-xl text-center bg-white dark:bg-gray-800">
                <FileText className="w-16 h-16 mx-auto text-secondary dark:text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-on-surface">Nenhuma solicitação encontrada</h3>
                <p className="text-secondary">Você ainda não fez nenhuma solicitação ao RH.</p>
              </div>
            ) : (
              minhasSolicitacoes.map((s) => (
                <div key={s.id} className="p-6 rounded-xl bg-white dark:bg-gray-800 border border-outline-variant">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-primary/10 text-primary rounded-lg">{tipoIcons[s.tipo]}</div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-on-surface">{tipoLabels[s.tipo]}</h3>
                        {getStatusBadge(s.status)}
                      </div>

                      <p className="text-on-surface dark:text-gray-300 mb-2">{s.descricao}</p>

                      <div className="text-sm text-secondary flex flex-wrap gap-x-6 gap-y-2">
                        {s.data_inicio && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {format(new Date(s.data_inicio + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR })}
                              {s.data_fim &&
                                ` até ${format(new Date(s.data_fim + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR })}`}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{format(new Date(s.data_solicitacao), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
                        </div>
                      </div>

                      {s.arquivo_url && (
                        <button
                          onClick={() => abrirDocumento(s.arquivo_url!)}
                          className="text-primary text-sm inline-flex items-center space-x-2 mt-2 hover:text-primary-dark transition-colors"
                        >
                          <File className="w-4 h-4" />
                          <span>Ver Documento</span>
                        </button>
                      )}

                      {s.resposta_rh && (
                        <div className="p-3 border border-outline-variant rounded-lg bg-surface-container-low mt-3">
                          <p className="text-xs font-medium text-on-surface mb-1">Resposta do RH:</p>
                          <p className="text-sm text-on-surface">{s.resposta_rh}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "admin" && isRH && <RHAdmin />}
      </div>
    </Layout>
  );
};